import { ElementType } from "react";
import {
    colors,
    disabledColors,
    disabledStates,
    loadingSizes,
    sizes,
} from "../../../styles/clickable";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    color?: keyof typeof colors;
    loading?: boolean;
    size?: keyof typeof sizes;
    ghost?: boolean;
    Icon?: ElementType;
    TrailingIcon?: ElementType;
    type?: "reset" | "button" | "submit";
}

export function ClickableContents({
    loading,
    Icon,
    size = "md",
    children,
    TrailingIcon,
}: Pick<
    ButtonProps,
    "loading" | "Icon" | "size" | "children" | "TrailingIcon"
>) {
    return (
        <>
            {loading ? (
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`animate-spin ${loadingSizes[size]} mx-auto`}
                    fill="currentColor"
                >
                    <path
                        d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"
                        opacity=".25"
                    />
                    <path d="M10.14,1.16a11,11,0,0,0-9,8.92A1.59,1.59,0,0,0,2.46,12,1.52,1.52,0,0,0,4.11,10.7a8,8,0,0,1,6.66-6.61A1.42,1.42,0,0,0,12,2.69h0A1.57,1.57,0,0,0,10.14,1.16Z" />
                </svg>
            ) : (
                <div className="flex items-center gap-x-2">
                    {Icon && <Icon className={`${loadingSizes[size]}`} />}
                    <div>{children}</div>
                    {TrailingIcon && (
                        <TrailingIcon className={`${loadingSizes[size]}`} />
                    )}
                </div>
            )}
        </>
    );
}

export function generateClickableClasses({ color, size, ghost, className }) {
    return `inline-flex items-center border focus:outline-hidden disabled:opacity-25 transition ease-in-out duration-150 cursor-pointer focus:shadow-focus-state shadow-blue-300 ${className} ${colors[color]} ${ghost ? disabledStates["ghost"] : disabledColors[color]} ${sizes[size]} ${ghost ? "!border-transparent" : ""}`;
}

export default function Button({
    children,
    className,
    color = "white",
    type = "button",
    size = "md",
    loading = false,
    ghost = false,
    Icon = undefined,
    TrailingIcon = undefined,
    ...rest
}: ButtonProps) {
    const classes = generateClickableClasses({ color, size, ghost, className });
    return (
        <button className={classes} type={type} {...rest} disabled={loading}>
            <ClickableContents
                loading={loading}
                Icon={Icon}
                TrailingIcon={TrailingIcon}
            >
                {children}
            </ClickableContents>
        </button>
    );
}
